import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid3X3,
  LineChart as LineChartIcon,
  Download,
  RefreshCw,
  Package,
  Group,
  Sigma,
  DollarSign,
  SlidersHorizontal,
  Hash,
  CalendarRange,
  Calendar,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import api from '../utils/api';
import { brandOptionsPath } from '../utils/brandOptionsSource';
import { useClient } from '../contexts/ClientContext';
import { getSemanaIso } from '../utils/dateFormat';
import CategoryPriceMatrix from './CategoryPriceMatrix';
import CategoryPriceTimeline from './CategoryPriceTimeline';
import { useAuth } from '../contexts/AuthContext';
import { saveTrendsPreferences, loadTrendsPreferences } from '../services/userPreferences';
import FilterBox, { FilterSlot } from './filters/FilterBox';
import FieldLabel from './filters/FieldLabel';
import { TENDENCIAS_CONFIG, DimensionKey, FilterControlKey } from '../config/filterBoxConfigs';
import { useFilterBoxState, InlineSourceData } from '../hooks/useFilterBoxState';
import { useFacetedFilters } from '../hooks/useFacetedFilters';
import { registerTrendsBridge, TrendsAxesSnapshot } from './trends/trendsFilterBridge';

interface TomaInfo {
  tomaId: number;
  fecha: string;
  tableName: string;
}

interface MatrixCell {
  value: number;
  count: number;
}

interface CategoryPriceTrendData {
  tomas: TomaInfo[];
  categories: string[];
  matrix: { [category: string]: { [tomaId: number]: MatrixCell | null } };
  skuMatrix?: { [category: string]: { [tomaId: number]: { sku: string; price: number }[] } } | null;
  analysisLevel: 'listings' | 'skus';
  metric: 'average' | 'median';
  priceType: 'current' | 'original';
  baselineTomaId: number | null;
}

interface CategoryPriceTrendCardProps {
  tableName: string;
  /** PT-#58 — Abre el modal combinado de filtros guardados (guardar+cargar+borrar). Se
   *  renderiza DENTRO del header del FilterBox, mismo lugar que Publicaciones/Estadísticas. */
  onOpenSavedFilters?: () => void;
  /** Deshabilita el botón mientras cargan los filtros guardados. */
  loadingSavedFilters?: boolean;
}

const CategoryPriceTrendCard: React.FC<CategoryPriceTrendCardProps> = ({ tableName, onOpenSavedFilters, loadingSavedFilters = false }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { selectedClient } = useClient(); // PT-#87 PR-d: enable_tags → fuente de Marca

  // State for data
  const [data, setData] = useState<CategoryPriceTrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generación diferida: la matriz/gráfico solo se regeneran al presionar "Confirmar".
  // appliedParamsKey = firma de los parámetros con los que se generó lo que se ve ahora.
  const [appliedParamsKey, setAppliedParamsKey] = useState<string | null>(null);

  // State for filters
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  // Samsung request B: subcategoría + segmento combinables junto a marca/categoría/sitio.
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [metric, setMetric] = useState<'average' | 'median'>('average');
  const [priceType, setPriceType] = useState<'current' | 'original'>('current');
  const [tomasLimit, setTomasLimit] = useState<number>(5);
  const [expandSkus, setExpandSkus] = useState(false);

  // Filtro por período de tiempo (issue #10)
  const [filterMode, setFilterMode] = useState<'tomas' | 'period'>('tomas');
  const [periodPreset, setPeriodPreset] = useState<'15d' | '1m' | '3m' | 'custom'>('1m');
  const [dateFrom, setDateFrom] = useState<string>(''); // 'YYYY-MM-DD'
  const [dateTo, setDateTo] = useState<string>(''); // 'YYYY-MM-DD'

  // New: Analysis level and baseline
  const [analysisLevel, setAnalysisLevel] = useState<'listings' | 'skus'>('skus');
  const [baselineTomaId, setBaselineTomaId] = useState<number | null>(null);

  // groupBy ahora decide SOLO el eje de agregación del gráfico (category/brand).
  // NO controla ya qué filtros están activos: todas las dimensiones son combinables.
  const [groupBy, setGroupBy] = useState<'category' | 'brand'>('category');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  // State for available filter options (+ counts para cascade treeMode).
  const [availableSites, setAvailableSites] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableSubcategories, setAvailableSubcategories] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  // Tendencias es MULTI-TOMA: los counts por-opción no tienen sentido cross-toma y
  // NO se muestran (a diferencia de Publicaciones/Estadísticas, single-toma con
  // faceted cross-filter). Por eso este card NO mantiene `*Meta` — pasa meta:null
  // a todos los dropdowns, lo que apaga tanto el total del header como el (N) por opción.

  // Caps per-cliente (idéntico a Dashboard/UnifiedFilterPanel): Adidas no tiene
  // subcategoría/segmento → esos dropdowns colapsan (no se renderizan dropdowns muertos).
  const [subcategoryFilterEnabled, setSubcategoryFilterEnabled] = useState(false);
  const [segmentFilterEnabled, setSegmentFilterEnabled] = useState(false);

  // State for view mode
  const [viewMode, setViewMode] = useState<'matrix' | 'timeline'>('matrix');

  // State for UI
  const [showFilters, setShowFilters] = useState(true);

  // Track if preferences have been loaded
  const preferencesLoaded = useRef(false);

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!currentUser?.uid || preferencesLoaded.current) return;

      const prefs = await loadTrendsPreferences(currentUser.uid);
      if (prefs) {
        setAnalysisLevel(prefs.analysisLevel);
        setViewMode(prefs.viewMode);
        setExpandSkus(prefs.expandSkus);
      }
      preferencesLoaded.current = true;
    };

    loadPreferences();
  }, [currentUser?.uid]);

  // Save preferences when they change
  useEffect(() => {
    if (!currentUser?.uid || !preferencesLoaded.current) return;

    const savePreferences = async () => {
      await saveTrendsPreferences(currentUser.uid, {
        analysisLevel,
        viewMode,
        expandSkus
      });
    };

    savePreferences();
  }, [currentUser?.uid, analysisLevel, viewMode, expandSkus]);

  // Helper: parsea la respuesta de los endpoints distinct-* SOLO a options planas.
  // Tendencias es multi-toma → NO usa counts (ver nota en el bloque de state arriba),
  // por eso pedimos el endpoint sin withCounts y solo extraemos los valores ordenados.
  const parseDistinctOptions = (data: any): string[] => {
    // Los endpoints distinct-* devuelven, SIN ?withCounts (que es como los llamamos acá),
    // arrays planos por dimensión: distinct-subcategory → { subcategories: string[] },
    // distinct-segmento → { segmentos: string[] }. La key `values` SOLO aparece con
    // ?withCounts=1 (que a propósito no pedimos: counts per-toma engañan en multi-toma).
    // Aceptamos las tres shapes para robustez y para no depender de withCounts.
    const raw: any[] =
      (Array.isArray(data?.subcategories) && data.subcategories) ||
      (Array.isArray(data?.segmentos) && data.segmentos) ||
      (Array.isArray(data?.values) && data.values) ||
      [];
    const options: string[] = [];
    for (const v of raw) {
      if (typeof v === 'string') options.push(v);
      else if (v && typeof v.value === 'string') options.push(v.value);
    }
    return options.sort((a, b) => {
      if (a === '(vacío)') return 1;
      if (b === '(vacío)') return -1;
      return a.localeCompare(b);
    });
  };

  // Carga de options + caps per-cliente. Reusa los MISMOS endpoints distinct-* que
  // Publicaciones (sin endpoint nuevo). El universo es multi-toma (no facets per-toma).
  useEffect(() => {
    if (!tableName) return;
    let cancelled = false;

    const loadOptionsAndCaps = async () => {
      // 1. Schema → columnConfig para derivar caps (subcategoría/segmento filtrables).
      let subEnabled = false;
      let segEnabled = false;
      // PT-#91: columnConfig + schema también deciden la fuente del desplegable de Marca.
      let brandCc: any = null;
      let brandSchema: any[] | null = null;
      try {
        const schemaRes = await api.get(`/api/tables/${tableName}/schema`);
        const cc = (schemaRes.data && (schemaRes.data as any).columnConfig) || null;
        brandCc = cc;
        brandSchema = Array.isArray((schemaRes.data as any)?.schema) ? (schemaRes.data as any).schema : null;
        const layout = cc?.filterLayout;
        const enabledInLayout = (key: string): boolean | undefined => {
          if (!layout?.rows) return undefined;
          for (const row of layout.rows) {
            const field = row.fields?.find((f: any) => f.key === key);
            if (field) return field.enabled;
          }
          return false;
        };
        subEnabled = !!(cc?.core?.subcategory?.filterable) && (enabledInLayout('subcategory') ?? true);
        segEnabled = !!(cc?.core?.segmento?.filterable) && (enabledInLayout('segment') ?? true);
      } catch { /* sin schema → caps false (colapsan) */ }
      if (cancelled) return;
      setSubcategoryFilterEnabled(subEnabled);
      setSegmentFilterEnabled(segEnabled);

      // 2. Sitios (options planas).
      try {
        const res = await api.get(`/api/tables/${tableName}/distinct-sites`);
        if (!cancelled) setAvailableSites(res.data.sites || []);
      } catch { /* noop */ }

      // 3. Marca (BUG-#7, Fix A / puente hacia PT-#55). Antes las opciones de Marca SOLO
      // se poblaban desde `trendData.categories` cuando groupBy='brand' (rama else del
      // effect de abajo) → en la vista por defecto (groupBy='category') el dropdown Marca
      // salía VACÍO y bloqueaba el pedido Samsung #2 (multi-marca combinable). Fix:
      // precargar las marcas al montar desde el MISMO origen que usa Publicaciones/Stats
      // (distinct-tag/MARCA = tags MARCA joineado a la toma, que para clientes con catalog
      // está sincronizado desde catalog_brand). El backend filtra por c.catalog_brand
      // (mismos valores). Endpoint sin ?withCounts → array plano de strings.
      // PT-#91: clientes sin merge de marca (Adidas, LG) usan /distinct/<Marca> (columna de la
      // toma, misma fuente que Publicaciones); ver utils/brandOptionsSource.
      try {
        const res = await api.get(brandOptionsPath(tableName, brandCc, brandSchema, selectedClient?.enable_tags !== false));
        const raw: string[] = Array.isArray(res.data) ? res.data : parseDistinctOptions(res.data);
        const brands = raw
          .filter((v): v is string => typeof v === 'string' && v !== '')
          .sort((a, b) => {
            if (a === '(vacío)') return 1;
            if (b === '(vacío)') return -1;
            return a.localeCompare(b);
          });
        if (!cancelled && brands.length) setAvailableBrands(brands);
      } catch { /* noop: sin tags MARCA el populate desde trendData sigue como fallback */ }

      // NOTA: category options NO se fetchean acá. Salen de `trendData.categories`
      // (el eje del query = valores de tag CATEGORIA), que es la fuente que matchea
      // lo que el backend filtra. distinct-categories lee la columna física cruda y para
      // clientes con tags (Samsung) NO coincide → se mantiene el populate desde la data.

      // 4. Subcategoría + segmento (solo si el cliente los habilita).
      if (subEnabled) {
        try {
          const res = await api.get(`/api/tables/${tableName}/distinct-subcategory`);
          if (!cancelled) setAvailableSubcategories(parseDistinctOptions(res.data));
        } catch { /* noop */ }
      } else if (!cancelled) {
        setAvailableSubcategories([]);
      }
      if (segEnabled) {
        try {
          const res = await api.get(`/api/tables/${tableName}/distinct-segmento`);
          if (!cancelled) setAvailableSegments(parseDistinctOptions(res.data));
        } catch { /* noop */ }
      } else if (!cancelled) {
        setAvailableSegments([]);
      }
    };

    loadOptionsAndCaps();
    return () => { cancelled = true; };
  }, [tableName, selectedClient?.enable_tags]);

  // AbortController ref to cancel in-flight requests when filters change
  const abortControllerRef = useRef<AbortController | null>(null);

  // Rango Personalizado inválido (hasta < desde con ambos seteados)
  const invalidCustomRange =
    filterMode === 'period' &&
    periodPreset === 'custom' &&
    !!dateFrom &&
    !!dateTo &&
    dateTo < dateFrom;

  // Firma de los parámetros que afectan la consulta al servidor.
  // Excluye baselineTomaId (lo maneja el server) y viewMode (es client-side).
  const currentParamsKey = useMemo(() => JSON.stringify({
    selectedSites: [...selectedSites].sort(),
    selectedCategories: [...selectedCategories].sort(),
    selectedBrands: [...selectedBrands].sort(),
    selectedSubcategories: [...selectedSubcategories].sort(),
    selectedSegments: [...selectedSegments].sort(),
    metric,
    priceType,
    tomasLimit,
    expandSkus,
    analysisLevel,
    groupBy,
    filterMode,
    periodPreset,
    dateFrom,
    dateTo,
  }), [selectedSites, selectedCategories, selectedBrands, selectedSubcategories, selectedSegments, metric, priceType, tomasLimit, expandSkus, analysisLevel, groupBy, filterMode, periodPreset, dateFrom, dateTo]);

  // Ref siempre actualizada con la firma actual (para leerla dentro de loadData sin recrearlo).
  const paramsKeyRef = useRef(currentParamsKey);
  paramsKeyRef.current = currentParamsKey;

  // Hay cambios sin aplicar si ya se generó algo y la firma actual difiere de la aplicada.
  const dirty = appliedParamsKey !== null && appliedParamsKey !== currentParamsKey;

  // Load trend data
  const loadData = useCallback(async () => {
    // No disparar con un rango Personalizado inválido
    if (invalidCustomRange) {
      return;
    }
    // Marcar la firma aplicada = la actual (deja de estar "dirty").
    setAppliedParamsKey(paramsKeyRef.current);

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (selectedSites.length > 0) {
        selectedSites.forEach(site => params.append('sites', site));
      }
      if (selectedCategories.length > 0) {
        selectedCategories.forEach(cat => params.append('categories', cat));
      }
      // PT-#57 R4-2 — "TODAS" en marcas: si están TODAS seleccionadas, es equivalente a
      // sin-filtro. Omitir el param evita explotar la URL con ~1500 `brands=` (que rompía
      // el request al presionar FILTRAR). Cuando es un subconjunto real, se envían normal.
      if (selectedBrands.length > 0 && selectedBrands.length < availableBrands.length) {
        selectedBrands.forEach(brand => params.append('brands', brand));
      }
      // Samsung request B: subcategoría + segmento combinables (se aplican en el backend
      // vía el LEFT JOIN catalog ya presente en la query de tendencias).
      if (selectedSubcategories.length > 0) {
        selectedSubcategories.forEach(s => params.append('subcategories', s));
      }
      if (selectedSegments.length > 0) {
        selectedSegments.forEach(s => params.append('segments', s));
      }
      params.append('metric', metric);
      params.append('priceType', priceType);
      if (filterMode === 'period') {
        if (periodPreset === 'custom') {
          if (dateFrom) params.append('dateFrom', dateFrom);
          if (dateTo) params.append('dateTo', dateTo);
        } else {
          params.append('periodPreset', periodPreset);
        }
      } else {
        params.append('limit', tomasLimit.toString());
      }
      params.append('expandSkus', expandSkus.toString());
      params.append('analysisLevel', analysisLevel);
      params.append('groupBy', groupBy);
      if (baselineTomaId) params.append('baselineTomaId', baselineTomaId.toString());

      const response = await api.get(`/api/statistics/category-price-trend?${params.toString()}`, {
        signal: controller.signal
      });

      // Only update state if this request wasn't cancelled
      if (!controller.signal.aborted) {
        setData(response.data);

        // Update baseline if it was adjusted by server
        if (response.data.baselineTomaId && response.data.baselineTomaId !== baselineTomaId) {
          setBaselineTomaId(response.data.baselineTomaId);
        }
      }
    } catch (err: any) {
      // Ignore aborted requests
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error loading category price trend:', err);
      if (!controller.signal.aborted) {
        setError(err instanceof Error ? err.message : t('statistics.category_price_trend.error_loading'));
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [selectedSites, selectedCategories, selectedBrands, availableBrands, selectedSubcategories, selectedSegments, metric, priceType, tomasLimit, expandSkus, analysisLevel, groupBy, baselineTomaId, filterMode, periodPreset, dateFrom, dateTo, invalidCustomRange, t]);

  // Ref siempre actualizada con el loadData más reciente (para llamarlo sin re-disparar efectos).
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  // PT-#58 — Ref al setter de dimensiones de la caja de filtros (useFilterBoxState). Se
  // asigna más abajo (el hook se declara después de este effect). El applySnapshot lo usa
  // para REHIDRATAR la caja al cargar un filtro guardado: sin esto, la caja seguiría
  // mostrando la selección vieja ("1 seleccionada") mientras el gráfico ya usa las dims
  // cargadas (desync box→state que reportó el QA T5).
  const setDimFilterRef = useRef<((dim: DimensionKey, value: string[]) => void) | null>(null);

  // PT-#58 — Bridge de saved filters. Registramos un getter (snapshot de los ejes de
  // Tendencias) + un applier (aplica un snapshot cargado y regenera). Dashboard los invoca
  // al guardar/cargar un filtro con scope 'multi'. Undefined-safe: cada campo del snapshot
  // es opcional → filtros legacy sin ejes de tendencias no rompen (el applier ignora los
  // campos ausentes y deja el estado actual).
  useEffect(() => {
    const unregister = registerTrendsBridge({
      getSnapshot: (): TrendsAxesSnapshot => ({
        sites: selectedSites,
        categories: selectedCategories,
        brands: selectedBrands,
        subcategories: selectedSubcategories,
        segments: selectedSegments,
        analysisLevel,
        groupBy,
        metric,
        priceType,
        tomasLimit,
        expandSkus,
        filterMode,
        periodPreset,
        dateFrom,
        dateTo,
      }),
      applySnapshot: (snap: TrendsAxesSnapshot) => {
        // Solo aplicamos los campos presentes (retrocompatibilidad con filtros viejos).
        // Para las 5 dimensiones seteamos el state "real" (fuente del fetch) Y rehidratamos
        // la caja de filtros (setDimFilterRef) para que las pastillas/dropdowns reflejen lo
        // cargado — cierra el desync box→state que el QA T5 detectó (gráfico usaba 2 dims
        // pero la caja mostraba 1). El guard de syncDim evita loop (mismo valor → no-op).
        const rehydrate = setDimFilterRef.current;
        if (Array.isArray(snap.sites)) { setSelectedSites(snap.sites); rehydrate?.('site', snap.sites); }
        if (Array.isArray(snap.categories)) { setSelectedCategories(snap.categories); rehydrate?.('category', snap.categories); }
        if (Array.isArray(snap.brands)) { setSelectedBrands(snap.brands); rehydrate?.('brand', snap.brands); }
        if (Array.isArray(snap.subcategories)) { setSelectedSubcategories(snap.subcategories); rehydrate?.('subcategory', snap.subcategories); }
        if (Array.isArray(snap.segments)) { setSelectedSegments(snap.segments); rehydrate?.('segment', snap.segments); }
        if (snap.analysisLevel) setAnalysisLevel(snap.analysisLevel);
        if (snap.groupBy) setGroupBy(snap.groupBy);
        if (snap.metric) setMetric(snap.metric);
        if (snap.priceType) setPriceType(snap.priceType);
        if (typeof snap.tomasLimit === 'number') setTomasLimit(snap.tomasLimit);
        if (typeof snap.expandSkus === 'boolean') setExpandSkus(snap.expandSkus);
        if (snap.filterMode) setFilterMode(snap.filterMode);
        if (snap.periodPreset) setPeriodPreset(snap.periodPreset);
        if (typeof snap.dateFrom === 'string') setDateFrom(snap.dateFrom);
        if (typeof snap.dateTo === 'string') setDateTo(snap.dateTo);
        // Regenerar tras aplicar los states (React ya habrá flusheado en el próximo tick).
        setTimeout(() => loadDataRef.current(), 0);
      },
    });
    return unregister;
  }, [selectedSites, selectedCategories, selectedBrands, selectedSubcategories, selectedSegments,
      analysisLevel, groupBy, metric, priceType, tomasLimit, expandSkus,
      filterMode, periodPreset, dateFrom, dateTo]);

  // Carga diferida: SOLO se genera automáticamente al montar o al cambiar de tabla.
  // Los cambios de filtros NO regeneran hasta presionar "Confirmar" (ver botón en el footer).
  useEffect(() => {
    loadDataRef.current();
  }, [tableName]);

  // Cleanup: abortar request en vuelo al desmontar.
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Recarga única después de aplicar las preferencias del usuario (analysisLevel/expandSkus las cambian).
  const prefsReloadDone = useRef(false);
  useEffect(() => {
    if (preferencesLoaded.current && !prefsReloadDone.current) {
      prefsReloadDone.current = true;
      loadDataRef.current();
    }
  }, [analysisLevel, expandSkus]);

  // When data loads and no baseline selected, set to first toma
  useEffect(() => {
    if (data?.tomas?.length && !baselineTomaId) {
      setBaselineTomaId(data.tomas[0].tomaId);
    }
  }, [data?.tomas, baselineTomaId]);

  // Update available categories/brands from data response
  // Only update if we have more items or if the list is empty
  useEffect(() => {
    if (data?.categories?.length) {
      if (groupBy === 'category') {
        setAvailableCategories(prev => {
          if (prev.length === 0) return data.categories;
          if (data.categories.length > prev.length) return data.categories;
          return prev;
        });
      } else {
        setAvailableBrands(prev => {
          if (prev.length === 0) return data.categories;
          if (data.categories.length > prev.length) return data.categories;
          return prev;
        });
      }
    }
  }, [data?.categories, groupBy]);

  // Get unit label based on analysis level
  const getUnitLabel = (count: number, plural: boolean = true): string => {
    if (analysisLevel === 'listings') {
      return plural
        ? t('statistics.category_price_trend.listings_plural')
        : t('statistics.category_price_trend.listings_singular');
    }
    return plural
      ? t('statistics.category_price_trend.skus_plural')
      : t('statistics.category_price_trend.skus_singular');
  };

  // Get subtitle based on analysis level and groupBy
  const getSubtitle = (): string => {
    if (analysisLevel === 'listings') {
      return groupBy === 'brand'
        ? t('statistics.category_price_trend.subtitle_listings_brand')
        : t('statistics.category_price_trend.subtitle_listings');
    }
    return groupBy === 'brand'
      ? t('statistics.category_price_trend.subtitle_skus_brand')
      : t('statistics.category_price_trend.subtitle_skus');
  };

  // Get title based on groupBy
  const getTitle = (): string => {
    return groupBy === 'brand'
      ? t('statistics.category_price_trend.title_by_brand')
      : t('statistics.category_price_trend.title');
  };

  // Export to XLSX (R3-1). El CSV previo escribía precios con formatPrice() → strings
  // tipo "$ 36.235" que Excel lee como TEXTO (no se pueden sumar). Ahora se emite un
  // .xlsx con celdas NUMÉRICAS tipadas (cell.t='n' + cell.z=PRICE_FORMAT), REUSANDO la
  // MISMA librería (xlsx-js-style) y el MISMO formato de moneda es-AR (LCID 2C0A) que
  // src/utils/exportToExcel.ts aplica en Publicaciones. El símbolo/miles se aplica como
  // formato de celda, no concatenando al texto → suma en cualquier locale.
  const handleExportExcel = () => {
    if (!data) return;

    const { tomas, categories, matrix } = data;

    // Formato currency es-AR forzado via LCID 2C0A (idéntico a exportToExcel.ts PRICE_FORMAT):
    // "$999.999" con separador de miles de Argentina, independiente del locale del sistema.
    // Sin decimales (la métrica de tendencias se muestra redondeada).
    const PRICE_FORMAT = '[$-2C0A]"$"#,##0';
    const INTEGER_FORMAT = '0';

    // Translated labels
    const unitLabel = analysisLevel === 'listings'
      ? t('statistics.category_price_trend.listings_plural')
      : t('statistics.category_price_trend.skus_plural');

    const analysisLevelLabel = analysisLevel === 'listings'
      ? t('statistics.category_price_trend.export.level_listings_market')
      : t('statistics.category_price_trend.export.level_skus_product');

    const metricLabel = metric === 'average'
      ? t('statistics.category_price_trend.metric_average')
      : t('statistics.category_price_trend.metric_median');

    const priceTypeLabel = priceType === 'current'
      ? t('statistics.category_price_trend.price_current')
      : t('statistics.category_price_trend.price_original');

    // #7 — La primera columna es la dimensión de agrupación (Categoría o Marca según
    // groupBy), no siempre "Categoría". Antes el header mentía cuando se agrupaba por marca.
    const dimLabel = groupBy === 'brand'
      ? t('statistics.category_price_trend.group_by_brand')
      : t('statistics.category_price_trend.group_by_category');

    // #7 — Contexto de filtros aplicados, para que el sheet sea auto-descriptivo.
    const filterContext: string[] = [];
    if (selectedSites.length) filterContext.push(`Sitios: ${selectedSites.join(' | ')}`);
    if (selectedBrands.length) filterContext.push(`Marcas: ${selectedBrands.join(' | ')}`);
    if (selectedCategories.length) filterContext.push(`Categorías: ${selectedCategories.join(' | ')}`);
    if (selectedSubcategories.length) filterContext.push(`Subcategorías: ${selectedSubcategories.join(' | ')}`);
    if (selectedSegments.length) filterContext.push(`Segmentos: ${selectedSegments.join(' | ')}`);

    // AoA (array de arrays) tipado: cada celda es un objeto SheetJS ({ t, v, z }).
    // t='n' → number; t='s' → string. Los precios (cell.value) y counts (cell.count)
    // van como NUMBER crudo con formato de celda numérico (nunca string con símbolo).
    type XlsxCell = { t: 's' | 'n'; v: string | number; z?: string; s?: any };
    const S = (v: string): XlsxCell => ({ t: 's', v });
    const NUM = (v: number, z: string): XlsxCell => ({ t: 'n', v, z, s: { numFmt: z } });

    const HEADER_STYLE = {
      font: { bold: true, color: { rgb: '000000' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'D9D9D9' } },
      alignment: { vertical: 'center', horizontal: 'left' },
    };
    const HEAD = (v: string): XlsxCell => ({ t: 's', v, s: HEADER_STYLE });

    const aoa: XlsxCell[][] = [];

    // Filas de contexto (encabezado del sheet).
    aoa.push([S(`${t('statistics.category_price_trend.export.analysis_level')}: ${analysisLevelLabel}`)]);
    aoa.push([S(`${t('statistics.category_price_trend.group_by')}: ${dimLabel}`)]);
    aoa.push([S(`${t('statistics.category_price_trend.export.metric_label')}: ${metricLabel}`)]);
    aoa.push([S(`${t('statistics.category_price_trend.export.price_type_label')}: ${priceTypeLabel}`)]);
    filterContext.forEach(line => aoa.push([S(line)]));
    aoa.push([]); // fila en blanco

    // Matriz de precios: header (dimensión + fechas) + fila Semana (ISO 8601 `AAAA-Www`) + una fila por categoría/marca.
    // PT-#63: en el pivot las fechas son headers por-toma → la semana ISO va como sub-fila rotulada bajo las fechas.
    aoa.push([HEAD(dimLabel), ...tomas.map(toma => HEAD(formatDate(toma.fecha)))]);
    aoa.push([HEAD('Semana'), ...tomas.map(toma => S(getSemanaIso(toma.fecha)))]);
    categories.forEach(cat => {
      const row: XlsxCell[] = [S(cat)];
      for (const toma of tomas) {
        const cell = matrix[cat]?.[toma.tomaId];
        // Precio como NUMBER tipado (no "$ 36.235"). Sin dato → celda vacía (string vacío).
        row.push(cell ? NUM(cell.value, PRICE_FORMAT) : S(''));
      }
      aoa.push(row);
    });

    aoa.push([]); // fila en blanco

    // Matriz de counts (mismas dimensiones), enteros tipados.
    aoa.push([
      HEAD(t('statistics.category_price_trend.export.count_header', { unit: unitLabel })),
      ...tomas.map(toma => HEAD(formatDate(toma.fecha))),
    ]);
    aoa.push([HEAD('Semana'), ...tomas.map(toma => S(getSemanaIso(toma.fecha)))]);
    categories.forEach(cat => {
      const row: XlsxCell[] = [S(`${cat} ${t('statistics.category_price_trend.export.count_suffix')}`)];
      for (const toma of tomas) {
        const cell = matrix[cat]?.[toma.tomaId];
        row.push(cell ? NUM(cell.count, INTEGER_FORMAT) : S(''));
      }
      aoa.push(row);
    });

    // Worksheet desde AoA tipado (aoa_to_sheet respeta cada objeto celda { t, v, z, s }).
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Anchos: primera columna (nombre) más ancha; el resto por longitud de fecha.
    const colCount = 1 + tomas.length;
    ws['!cols'] = Array.from({ length: colCount }, (_, i) =>
      i === 0 ? { wch: 32 } : { wch: 14 }
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tendencias');

    const filenamePrefix = t('statistics.category_price_trend.export.filename_prefix');
    // #7 — filename refleja la dimensión de agrupación (category/brand) + sitios.
    const sitesTag = selectedSites.length > 0 ? selectedSites.join('-') : 'all';
    const filename = `${filenamePrefix}-${groupBy}-${analysisLevel}-${sitesTag}-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  const formatDateFull = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // PT-#57 R2-4 — Cascade dimensional en TRENDS igual que Pubs/Stats. Levantamos facets
  // reales de la toma (`tableName`) para category/subcategory/segment: dan la co-ocurrencia
  // (qué subcategorías/segmentos existen bajo la categoría elegida) que `treeMode` usa para
  // acotar las options hijas. El endpoint /facets ya cross-filtra (cada facet excluye su
  // propio filtro), así que refleja la selección viva.
  const facetFilters = useMemo(() => ({
    brand: selectedBrands.join('|'),
    site: selectedSites.join('|'),
    category: selectedCategories.join('|'),
    subcategory: selectedSubcategories.join('|'),
    segment: selectedSegments.join('|'),
  }), [selectedBrands, selectedSites, selectedCategories, selectedSubcategories, selectedSegments]);

  const trendFacets = useFacetedFilters(facetFilters, {
    tableName,
    enabled: !!tableName,
    dimensions: ['category', 'subcategory', 'segment'],
    debounceMs: 300,
  });

  // GH#24 Fase 3 — Tendencias entra al MISMO pipeline de state que Publicaciones/
  // Estadísticas vía useFilterBoxState (source inline). Las 5 dimensiones son
  // COMBINABLES (Samsung request B); groupBy ya NO controla qué filtros están activos.
  // `inlineData` aporta options/counts/enabled (caps per-cliente); treeMode hace el
  // cascade category→subcategory→segment con los counts (igual que Publicaciones).
  // PT-#57 R2-4: category/subcategory/segment reciben `meta` REAL de trendFacets → el
  // cascade acota las hijas. Los counts NO se muestran (config.hideCounts en TENDENCIAS_CONFIG
  // le pasa meta=null al FilterDropdown), pero SÍ se usan para narrow. brand/site siguen
  // meta:null (no participan del árbol de co-ocurrencia).
  const inlineData = useMemo<InlineSourceData>(() => ({
    brand: { options: availableBrands, meta: null, enabled: true },
    site: { options: availableSites, meta: null, enabled: true },
    category: { options: availableCategories, meta: trendFacets.getMeta('category'), enabled: true },
    subcategory: { options: availableSubcategories, meta: trendFacets.getMeta('subcategory'), enabled: subcategoryFilterEnabled },
    segment: { options: availableSegments, meta: trendFacets.getMeta('segment'), enabled: segmentFilterEnabled },
  }), [availableBrands, availableSites, availableCategories,
       availableSubcategories, subcategoryFilterEnabled,
       availableSegments, segmentFilterEnabled, trendFacets]);

  const {
    dimensions: filterDimensions,
    activeFilters: boxActiveFilters,
    setDimFilter,
  } = useFilterBoxState({
    config: TENDENCIAS_CONFIG,
    tableName,
    enabled: true,
    inlineData,
  });
  // PT-#58 — Mantener la ref apuntando al setter vivo para que applySnapshot (declarado
  // antes en el árbol) pueda rehidratar la caja al cargar un filtro guardado.
  setDimFilterRef.current = setDimFilter;

  // Sync selección de la caja → state externo que arma la query (source of truth del fetch).
  // La caja tiene la selección viva; acá la reflejamos a los arrays que loadData serializa.
  const syncDim = (key: DimensionKey, setter: (v: string[]) => void, current: string[]) => {
    const next = boxActiveFilters[key] || [];
    if (next.length !== current.length || next.some((v, i) => v !== current[i])) setter(next);
  };
  useEffect(() => { syncDim('site', setSelectedSites, selectedSites); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('category', setSelectedCategories, selectedCategories); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('brand', setSelectedBrands, selectedBrands); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('subcategory', setSelectedSubcategories, selectedSubcategories); /* eslint-disable-next-line */ }, [boxActiveFilters]);
  useEffect(() => { syncDim('segment', setSelectedSegments, selectedSegments); /* eslint-disable-next-line */ }, [boxActiveFilters]);

  // Slots para TODOS los controles NO dimensionales que van DENTRO de la caja (PT-#57 bug3):
  // row 0 = Nivel de Análisis + Agrupar por · row 3 = Métrica + Tipo de Precio ·
  // row 4 = acotado temporal (Acotar por / Tomas / Período / custom / expandir SKUs).
  // Todos aplican `widthStyle` (lo pasa FilterBox por field.widthPct) para alinear a la grilla,
  // idéntico a Publicaciones/Estadísticas. Mismo estilo visual de labels/inputs que antes.
  const slots = useMemo<Partial<Record<FilterControlKey, FilterSlot>>>(() => ({
    // PT-#45 #13 — Nivel de Análisis y Agrupar por single-select. Opciones estáticas:
    // analysisLevel = listings/skus; groupBy = category/brand (backend solo soporta esos ejes).
    analysisLevel: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<Package className="h-3.5 w-3.5" />}>
          {t('statistics.category_price_trend.analysis_level')}
        </FieldLabel>
        <select
          value={analysisLevel}
          onChange={(e) => setAnalysisLevel(e.target.value as 'listings' | 'skus')}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="listings">{t('statistics.category_price_trend.level_listings')}</option>
          <option value="skus">{t('statistics.category_price_trend.level_skus')}</option>
        </select>
      </div>
    ),
    groupBy: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<Group className="h-3.5 w-3.5" />}>
          {t('statistics.category_price_trend.group_by')}
        </FieldLabel>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as 'category' | 'brand')}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="category">{t('statistics.category_price_trend.group_by_category')}</option>
          <option value="brand">{t('statistics.category_price_trend.group_by_brand')}</option>
        </select>
      </div>
    ),
    metric: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<Sigma className="h-3.5 w-3.5" />}>
          {t('statistics.category_price_trend.metric')}
        </FieldLabel>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value as 'average' | 'median')}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="average">{t('statistics.category_price_trend.metric_average')}</option>
          <option value="median">{t('statistics.category_price_trend.metric_median')}</option>
        </select>
      </div>
    ),
    priceType: (widthStyle) => (
      <div style={widthStyle}>
        <FieldLabel icon={<DollarSign className="h-3.5 w-3.5" />}>
          {t('statistics.category_price_trend.price_type')}
        </FieldLabel>
        <select
          value={priceType}
          onChange={(e) => setPriceType(e.target.value as 'current' | 'original')}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="current">{t('statistics.category_price_trend.price_current')}</option>
          <option value="original">{t('statistics.category_price_trend.price_original')}</option>
        </select>
      </div>
    ),
    // Bloque temporal completo como slot full-width con su propia sub-grilla alineada
    // (PT-#57 R2-3: cada control a 25% con minWidth 180px, MISMA grilla que las filas de
    // la caja; el contenedor va a width:100% para que las columnas alineen izq→der exacto).
    period: (_widthStyle) => {
      const cell: React.CSSProperties = { width: 'calc(25% - 1rem)', minWidth: '180px' };
      return (
        <div style={{ width: '100%' }}>
          <div className="flex gap-4 flex-wrap">
            {/* Modo de acotado temporal: Por tomas / Por período */}
            <div style={cell}>
              <FieldLabel icon={<SlidersHorizontal className="h-3.5 w-3.5" />}>Acotar por</FieldLabel>
              <select
                value={filterMode}
                onChange={(e) => setFilterMode(e.target.value as 'tomas' | 'period')}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="tomas">Por tomas</option>
                <option value="period">Por período</option>
              </select>
            </div>

            {/* Modo Por tomas: cantidad de tomas (5/10/15/20) */}
            {filterMode === 'tomas' && (
              <div style={cell}>
                <FieldLabel icon={<Hash className="h-3.5 w-3.5" />}>
                  {t('statistics.category_price_trend.tomas_limit')}
                </FieldLabel>
                <select
                  value={tomasLimit}
                  onChange={(e) => setTomasLimit(parseInt(e.target.value))}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            )}

            {/* Modo Por período: preset + rango Personalizado */}
            {filterMode === 'period' && (
              <>
                <div style={cell}>
                  <FieldLabel icon={<CalendarRange className="h-3.5 w-3.5" />}>Período</FieldLabel>
                  <select
                    value={periodPreset}
                    onChange={(e) => setPeriodPreset(e.target.value as '15d' | '1m' | '3m' | 'custom')}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="15d">Últimos 15 días</option>
                    <option value="1m">Último mes</option>
                    <option value="3m">Últimos 3 meses</option>
                    <option value="custom">Personalizado</option>
                  </select>
                </div>

                {periodPreset === 'custom' && (
                  <>
                    <div style={cell}>
                      <FieldLabel icon={<Calendar className="h-3.5 w-3.5" />}>Desde</FieldLabel>
                      <input
                        type="date"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums"
                      />
                    </div>
                    <div style={cell}>
                      <FieldLabel icon={<Calendar className="h-3.5 w-3.5" />}>Hasta</FieldLabel>
                      <input
                        type="date"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 tabular-nums"
                      />
                    </div>
                    {invalidCustomRange && (
                      <div className="flex items-end" style={cell}>
                        <p className="text-xs text-red-600">
                          La fecha "Hasta" no puede ser anterior a "Desde".
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Expand SKUs Toggle - Only for SKUs mode */}
            {analysisLevel === 'skus' && (
              <div className="flex items-end" style={cell}>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={expandSkus}
                    onChange={(e) => setExpandSkus(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {t('statistics.category_price_trend.expand_skus')}
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      );
    },
  }), [t, analysisLevel, groupBy, metric, priceType, filterMode, tomasLimit, periodPreset, dateFrom, dateTo, invalidCustomRange, expandSkus, setAnalysisLevel, setGroupBy, setMetric, setPriceType, setFilterMode, setTomasLimit, setPeriodPreset, setDateFrom, setDateTo, setExpandSkus]);

  return (
    <div className="space-y-4">
      {/* Title + Subtitle. El selector MATRIZ/TIMELINE se movió al header del card del
          gráfico (R2-2): es una decisión de VISTA del panel del gráfico, va alineado a
          la izquierda de ese header (junto al panel "Seleccionar categorías"). */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800">
          {getTitle()}
        </h3>
        <p className="text-sm text-gray-500">
          {getSubtitle()}
        </p>
      </div>

      {/* Panel de filtros: caja reusable con encabezado propio, IGUAL a Publicaciones/
          Estadísticas (PT-#57 bug4). TODOS los controles (dimensionales + Nivel/Agrupar +
          Métrica/TipoPrecio + acotado temporal) van DENTRO de la caja vía config+slots,
          alineados a la grilla (PT-#57 bug3). El botón "Confirmar" (generación diferida)
          va en el actionsSlot del panel "Filtros aplicados". */}
      <FilterBox
        config={TENDENCIAS_CONFIG}
        t={t}
        dimensions={filterDimensions}
        slots={slots}
        loading={trendFacets.loading}
        showHeader
        headerTitle={t('filters.title', 'Filtros')}
        onOpenSavedFilters={onOpenSavedFilters}
        savedFiltersLoading={loadingSavedFilters}
        collapsed={!showFilters}
        onToggleCollapse={() => setShowFilters(!showFilters)}
        actionsSlot={(
          <div className="flex items-center gap-3">
            {dirty && (
              <span className="text-xs text-amber-600 font-medium">Cambios sin aplicar</span>
            )}
            <button
              onClick={loadData}
              disabled={loading || invalidCustomRange}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                dirty
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              Confirmar
            </button>
          </div>
        )}
      />

      {/* Card del gráfico: header con selector MATRIZ/TIMELINE (izquierda, R2-2) +
          Export (derecha) + contenido (matriz/timeline). */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4">
            {/* R2-2: selector de vista al sector IZQUIERDO del header del card del gráfico. */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1 flex-shrink-0">
              <button
                onClick={() => setViewMode('matrix')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center space-x-1 ${
                  viewMode === 'matrix'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Grid3X3 className="h-4 w-4" />
                <span>{t('statistics.category_price_trend.matrix_view')}</span>
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center space-x-1 ${
                  viewMode === 'timeline'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <LineChartIcon className="h-4 w-4" />
                <span>{t('statistics.category_price_trend.timeline_view')}</span>
              </button>
            </div>
            <button
              onClick={handleExportExcel}
              disabled={!data || loading}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors disabled:opacity-50"
              title={t('statistics.category_price_trend.export_excel', 'Exportar a Excel')}
              aria-label={t('statistics.category_price_trend.export_excel', 'Exportar a Excel')}
            >
              <Download className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center space-y-3">
              <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
              <p className="text-gray-500">{t('statistics.category_price_trend.loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              {t('statistics.retry')}
            </button>
          </div>
        ) : !data || data.categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            {analysisLevel === 'skus' ? (
              <>
                <Package className="h-12 w-12 text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">{t('statistics.category_price_trend.no_skus_created')}</p>
                <p className="text-gray-400 text-sm mt-2">{t('statistics.category_price_trend.no_skus_hint')}</p>
              </>
            ) : (
              <p className="text-gray-500">{t('statistics.category_price_trend.no_data')}</p>
            )}
          </div>
        ) : viewMode === 'matrix' ? (
          <CategoryPriceMatrix
            data={data}
            formatPrice={formatPrice}
            formatDate={formatDate}
            expandSkus={expandSkus && analysisLevel === 'skus'}
            analysisLevel={analysisLevel}
            getUnitLabel={getUnitLabel}
          />
        ) : (
          <CategoryPriceTimeline
            data={data}
            formatPrice={formatPrice}
            formatDate={formatDate}
            formatDateFull={formatDateFull}
            analysisLevel={analysisLevel}
            baselineTomaId={baselineTomaId}
            getUnitLabel={getUnitLabel}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPriceTrendCard;
